import { HttpErrorResponse } from '@angular/common/http';
import { formatFullName } from '../../../helpers';
import { normalizePhonePayloadValue } from '../custom-field.validators';
import { BoardContact } from '../board-v3.types';

const CONTACT_ERROR_KEYS: Record<string, string> = {
  CONTACT_NOT_FOUND: 'board_v3_contact_error_not_found',
  COLUMN_CARD_NOT_FOUND: 'board_v3_contact_error_card_not_found',
  PHONE_NUMBER_INVALID: 'board_v3_contact_error_phone_invalid',
  EMAIL_INVALID: 'board_v3_contact_error_email_invalid',
  ADDRESS_INVALID: 'board_v3_contact_error_address_invalid',
};

type ContactNameSource = Pick<BoardContact, 'first_name' | 'last_name' | 'middle_name'>;

/** «Ім'я По батькові Прізвище» or «Прізвище Ім'я По батькові», following the company name format */
export function getContactName(contact: Partial<ContactNameSource> | null | undefined): string {
  if (!contact) return '';

  const firstName = [contact.first_name, contact.middle_name]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return formatFullName(firstName, contact.last_name);
}

export function formatContactPhone(value: unknown): string {
  const normalized = normalizePhonePayloadValue(value);
  if (!/^\+\d+$/.test(normalized)) return String(value ?? '');

  return `+${(normalized.slice(1).match(/\d{1,3}/g) ?? []).join(' ')}`;
}

export function getContactPhones(contact: BoardContact): string[] {
  return (contact.phones ?? []).map((phone) => phone.number_e164).filter(Boolean);
}

export function getContactEmails(contact: BoardContact): string[] {
  return (contact.emails ?? []).map((email) => email.email).filter(Boolean);
}

/** Translation key for a contact API error */
export function getContactErrorKey(error: unknown): string {
  const code = (error as HttpErrorResponse)?.error?.errors?.[0]?.code as string | undefined;
  return (code && CONTACT_ERROR_KEYS[code]) || 'errors.default_error';
}

/**
 * A 409 carries the contact that already owns the phone or email.
 * The payload shape is not final on the backend, so every known place is checked.
 */
export function getDuplicateContact(error: unknown): BoardContact | null {
  const response = error as HttpErrorResponse;
  if (response?.status !== 409) return null;

  const body = response.error ?? {};
  const candidates = [body.data?.contact, body.data, body.contact, body.errors?.[0]?.contact, body.errors?.[0]?.data];
  const contact = candidates.find((item) => item && typeof item === 'object' && typeof item.id === 'string');
  return (contact as BoardContact) ?? null;
}
